import { type ApolloClient, gql } from '@apollo/client';
import { WorkflowStates } from '@generated/graphql';
import {
  FETCH_ACCOUNT_GROUP_WITH_USER,
  FETCH_ACCOUNT_WITH_USER,
  FETCH_GOAL_WITH_USER,
  FETCH_HOUSEHOLD_WITH_USER,
  FETCH_SUB_ACCOUNT_WITH_USER,
} from '@ovComponents/5-page/pageConfiguration/pageConfiguration.queries';
import {
  PageObjectType,
  type TabInterface,
} from '@ovComponents/5-page/pageConfiguration/pageConfigurationTypes';
export type TabWorkflowUser = {
  userId: string;
  organizationId: string;
};

export type TabWorkflowUserParams = {
  pageType?: PageObjectType;
  userId?: string;
  objectId?: string;
  activeEntityId?: string;
  accountId?: string;
  goalId?: string;
  subAccountId?: string;
  clientGroupId?: string;
  accountGroupId?: string;
};

export type TabWorkflowCompletionInput = {
  objectId: string;
  objectType: 'USER' | 'HOUSEHOLD';
  workflowId: string;
  organizationId: string;
};

export const buildTabWorkflowCompletionInput = (
  params: TabWorkflowUserParams,
  tabWorkflowUser: TabWorkflowUser,
  workflowId: string,
): TabWorkflowCompletionInput | undefined => {
  if (params.pageType === PageObjectType.HOUSEHOLD) {
    const clientGroupId = params.clientGroupId || params.objectId;
    if (!clientGroupId || !tabWorkflowUser.organizationId) return undefined;
    return {
      objectId: clientGroupId,
      objectType: 'HOUSEHOLD',
      workflowId,
      organizationId: tabWorkflowUser.organizationId,
    };
  }

  return {
    objectId: tabWorkflowUser.userId,
    objectType: 'USER',
    workflowId,
    organizationId: tabWorkflowUser.organizationId,
  };
};

const FETCH_USER_ORGANIZATION = gql`
  query fetchUserOrganization($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        organization {
          id
        }
      }
    }
  }
`;

const toTabWorkflowUser = (user?: {
  id?: string;
  organization?: { id?: string };
}): TabWorkflowUser | undefined => {
  if (!user?.id || !user.organization?.id) return undefined;
  return { userId: user.id, organizationId: user.organization.id };
};

export const fetchTabWorkflowUser = async (
  client: ApolloClient<object>,
  params: TabWorkflowUserParams,
): Promise<TabWorkflowUser | undefined> => {
  let userObjectId: string | undefined;
  if (
    params.pageType &&
    [PageObjectType.INDIVIDUAL, PageObjectType.NON_INDIVIDUAL].includes(
      params.pageType,
    )
  )
    userObjectId = params.objectId;
  const syncUserId = params.userId || params.activeEntityId || userObjectId;
  if (syncUserId) {
    const result = await client.query({
      query: FETCH_USER_ORGANIZATION,
      variables: { userId: syncUserId },
      fetchPolicy: 'no-cache',
    });
    const user = toTabWorkflowUser(result.data?.fetchUser?.user);
    if (user) return user;
  }

  switch (params.pageType) {
    case PageObjectType.ACCOUNT: {
      const accountId = params.accountId || params.objectId;
      if (!accountId) return undefined;
      const result = await client.query({
        query: FETCH_ACCOUNT_WITH_USER,
        variables: { accountId },
        fetchPolicy: 'no-cache',
      });
      return toTabWorkflowUser(result.data?.fetchAccount?.account?.user);
    }
    case PageObjectType.GOAL: {
      const goalId = params.goalId || params.objectId;
      if (!goalId) return undefined;
      const result = await client.query({
        query: FETCH_GOAL_WITH_USER,
        variables: { goalId },
        fetchPolicy: 'no-cache',
      });
      return toTabWorkflowUser(result.data?.fetchGoal?.goal?.user);
    }
    case PageObjectType.SUB_ACCOUNT: {
      const subAccountId = params.subAccountId || params.objectId;
      if (!subAccountId) return undefined;
      const result = await client.query({
        query: FETCH_SUB_ACCOUNT_WITH_USER,
        variables: { subAccountId },
        fetchPolicy: 'no-cache',
      });
      return toTabWorkflowUser(
        result.data?.fetchSubAccount?.subAccount?.account?.user,
      );
    }
    case PageObjectType.HOUSEHOLD: {
      const clientGroupId = params.clientGroupId || params.objectId;
      if (!clientGroupId) return undefined;
      const result = await client.query({
        query: FETCH_HOUSEHOLD_WITH_USER,
        variables: { clientGroupId },
        fetchPolicy: 'no-cache',
      });
      return toTabWorkflowUser(
        result.data?.fetchClientGroup?.clientGroup?.primaryUser,
      );
    }
    case PageObjectType.ACCOUNT_GROUP: {
      const accountGroupId = params.accountGroupId || params.objectId;
      if (!accountGroupId) return undefined;
      const result = await client.query({
        query: FETCH_ACCOUNT_GROUP_WITH_USER,
        variables: { accountGroupId },
        fetchPolicy: 'no-cache',
      });
      const accountGroup = result.data?.fetchAccountGroup?.accountGroup;
      return (
        toTabWorkflowUser(accountGroup?.user) ??
        toTabWorkflowUser(accountGroup?.householdClientGroup?.primaryUser)
      );
    }
    default:
      return undefined;
  }
};

export const isWorkflowTab = (tab: TabInterface) => !!tab.workflow?.id;

export const isActiveWorkflowTab = (tab: TabInterface) =>
  isWorkflowTab(tab) && tab.workflow?.state === WorkflowStates.Active;

export const isWorkflowTabDisabledOnLivePage = (
  tab: TabInterface,
  {
    canTriggerWorkflow,
    isWorkflowTabTriggerReady,
  }: {
    canTriggerWorkflow: boolean;
    isWorkflowTabTriggerReady: boolean;
  },
) => {
  if (!isWorkflowTab(tab)) return false;
  return (
    !canTriggerWorkflow ||
    !isActiveWorkflowTab(tab) ||
    !isWorkflowTabTriggerReady
  );
};
